/**
 * FontConfigFromProjectProvider
 *
 * Self-contained FontConfigProvider for Studio editor surfaces (canvas
 * nodes, screen-editor dialog, template panel, apply-theme dialog): owns
 * its data the way FontLabelsProvider does — subscribes to the project's
 * fontConfig, detects the preview platform, resolves fallback chains and
 * active web fonts, and feeds them to the plain FontConfigProvider.
 *
 * One wrap covers every V2ScreenView beneath it; previews then honor the
 * project's per-platform font mappings (previously they silently rendered
 * DEFAULT chains) and load self-hosted web fonts.
 *
 * @module font-config/context
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { FontConfigProvider } from './FontConfigContext';
import { useFontConfig } from '../hooks/useFontConfig';
import { buildFallbackChains } from '../data/systemFontCatalog';
import { detectPlatform } from '../services/platformDetector';
import { collectActiveWebFonts } from '../utils/collectWebFonts';
import { getStudioWebFontsEnv } from '../utils/studioWebFontsEnv';
import type { PlatformId } from '../types/fontConfig.types';

export interface FontConfigFromProjectProviderProps {
  projectId: string;
  /** Force a platform for the preview; defaults to the editor's own OS. */
  previewPlatform?: PlatformId;
  children: ReactNode;
}

export function FontConfigFromProjectProvider({
  projectId,
  previewPlatform,
  children,
}: FontConfigFromProjectProviderProps) {
  const { config: fontConfig } = useFontConfig(projectId);

  const platform = useMemo(
    () => previewPlatform ?? detectPlatform(
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
    ),
    [previewPlatform],
  );

  const fontOverrides = useMemo(
    () => buildFallbackChains(fontConfig ?? null, platform),
    [fontConfig, platform],
  );

  const webFonts = useMemo(
    () => collectActiveWebFonts(fontConfig ?? null, {
      platforms: [platform],
      env: getStudioWebFontsEnv(),
    }),
    [fontConfig, platform],
  );

  return (
    <FontConfigProvider fontOverrides={fontOverrides} platform={platform} webFonts={webFonts}>
      {children}
    </FontConfigProvider>
  );
}
